import { useState } from 'react';
import axios from 'axios';
import { Mail, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsletterSignup({ source = 'blog', variant = 'card' }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (busy || done) return;
    if (!email.trim()) {
      toast.error('Please enter an email.');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email: email.trim(), source });
      setDone(true);
      if (res.data.already_subscribed) {
        toast.success('You\'re already on the list — thanks!');
      } else {
        toast.success('You\'re in. We\'ll keep it short and useful.');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not subscribe. Try again.';
      toast.error(typeof msg === 'string' ? msg : 'Could not subscribe.');
    } finally {
      setBusy(false);
    }
  };

  if (variant === 'inline') {
    return (
      <form onSubmit={submit} className="bg-stone-100 border border-stone-200 rounded-2xl p-5 my-6" data-testid="newsletter-inline">
        <div className="flex items-center gap-2 mb-2">
          <Mail size={15} className="text-orange-600" />
          <span className="font-display font-bold tracking-tight">Get one good paperwork tip a week</span>
        </div>
        <p className="text-sm text-stone-600 mb-3">No fluff, no spam. Unsubscribe in one click.</p>
        <div className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@work.com"
            className="flex-1 bg-white border border-stone-200 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
            disabled={done}
            data-testid="newsletter-email-input"
          />
          <button
            type="submit"
            disabled={busy || done}
            className="px-5 py-2.5 rounded-full bg-stone-900 hover:bg-stone-800 text-white text-sm font-medium btn-clay disabled:opacity-60"
            data-testid="newsletter-submit"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : done ? <Check size={14} /> : 'Subscribe'}
          </button>
        </div>
      </form>
    );
  }

  // card variant (default)
  return (
    <div className="relative overflow-hidden bg-stone-900 text-white rounded-3xl p-8 sm:p-10" data-testid="newsletter-card">
      <div className="absolute -bottom-20 -right-16 w-72 h-72 bg-orange-500/20 blur-3xl rounded-full" />
      <div className="relative max-w-md">
        <div className="text-xs uppercase tracking-[0.18em] text-orange-400 mb-3">The Paperwork Letter</div>
        <h3 className="font-display font-bold text-3xl sm:text-4xl tracking-tight leading-tight">
          One tip a week.<br />
          <span className="text-orange-400 italic font-serif font-medium">No fluff.</span>
        </h3>
        <p className="text-stone-300 mt-3 leading-relaxed">
          A short, useful note every Tuesday on the contracts, forms, and clauses that matter.
        </p>
        <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@work.com"
            className="flex-1 bg-white/10 border border-white/20 text-white placeholder:text-stone-400 rounded-full px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            disabled={done}
            data-testid="newsletter-email-input"
          />
          <button
            type="submit"
            disabled={busy || done}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium btn-clay disabled:opacity-60"
            data-testid="newsletter-submit"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {done ? <><Check size={14} /> Subscribed</> : 'Subscribe'}
          </button>
        </form>
        <p className="text-xs text-stone-500 mt-3">Unsubscribe with one click. We never sell your email.</p>
      </div>
    </div>
  );
}
